import { ETAPAS_DO_FUNIL, type EtapaDoFunil, type Funil } from "@base/contracts";

export const ROTULO_DA_ETAPA: Readonly<Record<EtapaDoFunil, string>> = {
  gerenciador: "Gerenciador",
  crm: "CRM",
  mql: "MQL",
  agendado: "Agendado",
  consulta: "Consulta realizada",
  venda: "Venda",
};

export const DESCRICAO_DA_ETAPA: Readonly<Record<EtapaDoFunil, string>> = {
  gerenciador: "Leads no gerenciador de anúncios",
  crm: "Leads que entraram no CRM",
  mql: "Leads qualificados pelo marketing",
  agendado: "Consultas agendadas",
  consulta: "Pacientes que compareceram",
  venda: "Tratamentos fechados",
};

export type EtapaCalculada = {
  readonly etapa: EtapaDoFunil;
  readonly rotulo: string;
  readonly descricao: string;
  /** `null` quando a fonte da etapa nao esta conectada (Gerenciador sem Meta/Google). */
  readonly quantidade: number | null;
  /** Fracao em relacao a primeira etapa (altura da barra, proporcao real). */
  readonly proporcaoDoTopo: number;
};

export type Conversao = {
  readonly de: EtapaDoFunil;
  readonly para: EtapaDoFunil;
  /** Nome da taxa, no padrao pedido pelo cliente ("Taxa de MQL"). */
  readonly nome: string;
  /** Caminho da conversao ("CRM → MQL"), para deixar o denominador explicito. */
  readonly rotulo: string;
  /** Como a taxa e calculada, em palavras, para a ajuda contextual. */
  readonly formula: string;
  /** Fracao (0.42 = 42%); `null` quando a etapa de origem esta vazia. */
  readonly taxa: number | null;
};

/** Nome de cada taxa, pela etapa de destino (documentacao funcional, RF-09). */
const NOME_DA_TAXA: Readonly<Record<Exclude<EtapaDoFunil, "gerenciador">, string>> = {
  crm: "Taxa de lead",
  mql: "Taxa de MQL",
  agendado: "Taxa de agenda",
  consulta: "Taxa de consulta realizada",
  venda: "Taxa de vendas",
};

export type CustoDaEtapa = {
  readonly etapa: EtapaDoFunil;
  /** Sigla curta para a coluna ("CPL", "CPA"). */
  readonly sigla: string;
  readonly nome: string;
  /** Em reais. `null` sem investimento ou sem resultado na etapa. */
  readonly valor: number | null;
};

/**
 * Custos por etapa (RF-10). A etapa Gerenciador nao tem custo proprio: os leads
 * do gerenciador sao a base de midia, e o primeiro custo e o CPL do CRM. A sigla
 * de cada custo ainda precisa ser confirmada com o cliente.
 */
const CUSTO_DA_ETAPA: Readonly<Record<Exclude<EtapaDoFunil, "gerenciador">, { sigla: string; nome: string }>> = {
  crm: { sigla: "CPL", nome: "Custo por lead" },
  mql: { sigla: "Custo/MQL", nome: "Custo por MQL" },
  agendado: { sigla: "Custo/agend.", nome: "Custo por agendamento" },
  consulta: { sigla: "Custo/consulta", nome: "Custo por consulta" },
  venda: { sigla: "CPA", nome: "Custo por aquisição de paciente" },
};

const taxa = (para: number | null, de: number | null): number | null =>
  para !== null && de !== null && de > 0 ? para / de : null;

/**
 * Largura visual de cada barra (0–1). Usa a raiz da proporção pra etapa
 * intermediária não virar filete, e desce um degrau em relação à de cima
 * mesmo quando Gerenciador e CRM têm o mesmo número.
 */
export function largurasDoFunil(
  etapas: readonly Pick<EtapaCalculada, "quantidade" | "proporcaoDoTopo">[],
): number[] {
  const minimo = 0.4;
  const queda = 0.09;
  let anterior: number | null = null;
  return etapas.map((etapa) => {
    if (etapa.quantidade === null) return 1;
    const pelosDados = minimo + (1 - minimo) * Math.sqrt(Math.max(0, etapa.proporcaoDoTopo));
    const teto = anterior === null ? 1 : anterior - queda;
    const largura = Math.min(Math.max(pelosDados, minimo), teto);
    anterior = largura;
    return largura;
  });
}

export function calcularEtapas(funil: Funil): readonly EtapaCalculada[] {
  // Sem gerenciador, o topo do funil passa a ser o CRM.
  const topo = funil.gerenciador ?? funil.crm;
  return ETAPAS_DO_FUNIL.map((etapa) => {
    const quantidade = funil[etapa];
    return {
      etapa,
      rotulo: ROTULO_DA_ETAPA[etapa],
      descricao: DESCRICAO_DA_ETAPA[etapa],
      quantidade,
      proporcaoDoTopo: quantidade !== null && topo > 0 ? quantidade / topo : 0,
    };
  });
}

/** Conversao de cada etapa para a seguinte. */
export function conversoesEntreEtapas(funil: Funil): readonly Conversao[] {
  return ETAPAS_DO_FUNIL.slice(1).map((para, indice) => {
    const de = ETAPAS_DO_FUNIL[indice] as EtapaDoFunil;
    return {
      de,
      para,
      nome: NOME_DA_TAXA[para as Exclude<EtapaDoFunil, "gerenciador">],
      rotulo: `${ROTULO_DA_ETAPA[de]} → ${ROTULO_DA_ETAPA[para]}`,
      formula: `${ROTULO_DA_ETAPA[para]} ÷ ${ROTULO_DA_ETAPA[de]}`,
      taxa: taxa(funil[para], funil[de]),
    };
  });
}

/** Taxa global (RF-09): o quanto do que entra no CRM vira venda. */
export function conversaoCrmParaVenda(funil: Funil): Conversao {
  return {
    de: "crm",
    para: "venda",
    nome: "Taxa global",
    rotulo: "CRM → Venda",
    formula: "Venda ÷ CRM",
    taxa: taxa(funil.venda, funil.crm),
  };
}

/**
 * Investimento ÷ resultados de cada etapa. Calculado do total, nunca da media de
 * custos unitarios, para os numeros consolidados baterem com a soma das partes.
 */
export function custosPorEtapa(funil: Funil, investimentoCentavos: number | null): readonly CustoDaEtapa[] {
  return ETAPAS_DO_FUNIL.slice(1).map((etapa) => {
    const definicao = CUSTO_DA_ETAPA[etapa as Exclude<EtapaDoFunil, "gerenciador">];
    const quantidade = funil[etapa];
    return {
      etapa,
      sigla: definicao.sigla,
      nome: definicao.nome,
      valor:
        investimentoCentavos !== null && investimentoCentavos > 0 && quantidade !== null && quantidade > 0
          ? investimentoCentavos / 100 / quantidade
          : null,
    };
  });
}

/** Metas do funil no periodo, nas mesmas formas do realizado. */
export type MetaDoFunil = {
  readonly etapas: ReadonlyMap<EtapaDoFunil, number>;
  /** Taxa meta de cada conversao, pela etapa de destino. */
  readonly conversoes: ReadonlyMap<EtapaDoFunil, number | null>;
  /** Custo meta de cada etapa, em reais. */
  readonly custos: ReadonlyMap<EtapaDoFunil, number | null>;
  readonly global: number | null;
};

/**
 * Da meta do periodo (Funil Projetado) para as metas de cada parte do funil.
 * Usa as MESMAS funcoes do realizado sobre um "funil de meta": taxa e custo da
 * meta seguem exatamente a regra do numero que comparam.
 */
export function metaDoFunil(
  meta: { readonly funil: Funil; readonly investimentoCentavos: number | null } | null,
): MetaDoFunil | null {
  if (!meta) return null;
  return {
    etapas: new Map(
      ETAPAS_DO_FUNIL.flatMap((etapa) => {
        const valor = meta.funil[etapa];
        return valor === null ? [] : [[etapa, valor] as const];
      }),
    ),
    conversoes: new Map(conversoesEntreEtapas(meta.funil).map((conversao) => [conversao.para, conversao.taxa])),
    custos: new Map(custosPorEtapa(meta.funil, meta.investimentoCentavos).map((custo) => [custo.etapa, custo.valor])),
    global: conversaoCrmParaVenda(meta.funil).taxa,
  };
}
